import { Controller } from 'stimulus';

// Table-side half of the org-group compare feature. The comparison table and
// its chart now load in separate turbo frames, so the table can't be a target
// of the chart's controller. Instead this controller emits `compare:toggle`
// on window when a checkbox changes, and listens for `compare:state` from the
// chart to keep its checkboxes and colour swatches in sync.
//
// The checkboxes/swatches arrive asynchronously: the table is a Tabulator that
// rebuilds its body from the source `<td>` HTML on `tableBuilt`, so a target
// can connect *after* the chart has already broadcast its state. The latest
// known state is therefore cached on the controller and (re)applied to each
// target as it connects, rather than only when `compare:state` fires.

// Grace period after which the checkboxes are enabled even if the chart frame
// never reported its state. Comfortably longer than a healthy frame load, so
// it only trips when the chart genuinely failed (errored or never loaded),
// keeping compare from being stuck disabled forever.
const READY_FALLBACK_MS = 8000;

export default class extends Controller {
  static targets = ['checkbox', 'swatch'];

  // State the target-connected callbacks read. Set in `initialize` (not
  // `connect`) because Stimulus fires `*TargetConnected` for already-present
  // targets — the source table's rows — before `connect` runs.
  initialize() {
    this.ready = false;
    this.selectedIds = new Set();
    this.colors = {};
  }

  connect() {
    window.addEventListener('compare:state', this.onState);
    // Enable the checkboxes anyway if the chart frame never reports state, so
    // a failed/erroring chart frame doesn't leave compare permanently disabled.
    this.readyFallback = setTimeout(() => this.markReady(), READY_FALLBACK_MS);
    // The chart frame may have connected first; ask it to replay its state.
    window.dispatchEvent(new CustomEvent('compare:sync-request'));
  }

  disconnect() {
    window.removeEventListener('compare:state', this.onState);
    clearTimeout(this.readyFallback);
  }

  // Tabulator (re)builds the body after this controller connects, so apply the
  // current state to each checkbox/swatch when it actually arrives. Keeping
  // checkboxes disabled until the chart is ready avoids toggles being dropped
  // before the chart frame is listening.
  checkboxTargetConnected(checkbox) {
    checkbox.disabled = !this.ready;
    checkbox.checked = this.selectedIds.has(checkbox.dataset.compareId);
  }

  swatchTargetConnected(swatch) {
    this.paintSwatch(swatch);
  }

  toggle(event) {
    const compareId = event.currentTarget.dataset.compareId;
    if (!compareId) return;

    window.dispatchEvent(
      new CustomEvent('compare:toggle', {
        detail: { compareId, checked: event.currentTarget.checked },
      })
    );
  }

  markReady() {
    if (this.ready) return;
    this.ready = true;
    this.checkboxTargets.forEach((checkbox) => {
      checkbox.disabled = false;
    });
  }

  onState = (event) => {
    clearTimeout(this.readyFallback);
    const { selectedIds = [], colors = {} } = event.detail || {};
    this.selectedIds = new Set(selectedIds);
    this.colors = colors;
    this.markReady();

    this.checkboxTargets.forEach((checkbox) => {
      checkbox.checked = this.selectedIds.has(checkbox.dataset.compareId);
    });
    this.swatchTargets.forEach((swatch) => this.paintSwatch(swatch));
  };

  paintSwatch(swatch) {
    const color = this.colors[swatch.dataset.compareId];
    swatch.style.backgroundColor = color || '';
    swatch.classList.toggle('is-empty', !color);
  }
}
